import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { toast } from "react-toastify";
import { DeleteEmailTemplate, EmailTemplate, PostEmailTemplate } from "../../../types/EmailTemplateTypes";
import { fetchEmailTemplates, createEmailTemplate, deleteEmailTemplate, updateEmailTemplate, postEmailTemplates, updateEmailTemplateOrder } from "./emailTemplateAsyncThunk";
import { RequestResults } from "../../common/RequestStatus";

interface EmailTemplateState {
  data: EmailTemplate[];
  status: RequestResults;
  loading: boolean;
  error: string | null;
  postEmailTemplate: PostEmailTemplate | {};
}

const initialState: EmailTemplateState = {
  data: [],
  status: RequestResults.idle,
  loading: false,
  error: null,
  postEmailTemplate: {},
};

// Slice
const EmailTemplateSlice = createSlice({
  name: "emailTemplate",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Fetch EmailTemplates
    builder
      .addCase(fetchEmailTemplates.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(fetchEmailTemplates.fulfilled, (state, action: PayloadAction<EmailTemplate[]>) => {
        state.status = RequestResults.success;
        state.data = action.payload;
      })
      .addCase(fetchEmailTemplates.rejected, (state, action) => {
        state.status = RequestResults.failed;
        toast.error(action.payload || "Failed to fetch EmailTemplates");
        state.error = action.payload || "Failed to fetch EmailTemplates";
      });

    // Post EmailTemplates
    builder
      .addCase(postEmailTemplates.pending, (state) => {
        state.status = RequestResults.pending;
        state.error = null;
      })
      .addCase(postEmailTemplates.fulfilled, (state, action: PayloadAction<PostEmailTemplate>) => {
        state.status = RequestResults.success;
        state.postEmailTemplate = action.payload;
      })
      .addCase(postEmailTemplates.rejected, (state, action) => {
        state.status = RequestResults.failed;
        toast.error(action.payload || "Failed to post EmailTemplates");
        state.error = action.payload || "Failed to post EmailTemplates";
      });

    // Add EmailTemplate
    builder
      .addCase(createEmailTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(createEmailTemplate.fulfilled, (state, action: PayloadAction<EmailTemplate>) => {
        state.loading = false;
        state.data.push(action.payload);
      })
      .addCase(createEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to add EmailTemplate");
        state.error = action.payload || "Failed to add EmailTemplate";
      });

    // Update EmailTemplate
    builder
      .addCase(updateEmailTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateEmailTemplate.fulfilled, (state, action: PayloadAction<EmailTemplate>) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
        state.loading = false;
      })
      .addCase(updateEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to update EmailTemplate");
        state.error = action.payload || "Failed to update EmailTemplate";
      });

    // Delete EmailTemplate
    builder
      .addCase(deleteEmailTemplate.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteEmailTemplate.fulfilled, (state, action: PayloadAction<DeleteEmailTemplate>) => {
        const index = state.data.findIndex((item) => item.id === action.payload.id);
        if (index !== -1) {
          state.data = state.data.filter((item) => item.id !== action.payload.id);
        }
        state.loading = false;
      })
      .addCase(deleteEmailTemplate.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload || "Failed to delete EmailTemplate");
        state.error = action.payload || "Failed to delete EmailTemplate";
      })
      //Email Template Update Order
      .addCase(updateEmailTemplateOrder.pending, (state) => {
        state.status = RequestResults.pending;
        state.loading = true;
      })
      .addCase(updateEmailTemplateOrder.fulfilled, (state, action: PayloadAction<EmailTemplate[]>) => {
        state.data = action.payload;
        state.status = RequestResults.success;
        state.loading = false;
        toast.success("Email template order updated successfully");
      })
      .addCase(updateEmailTemplateOrder.rejected, (state, action) => {
        state.status = RequestResults.failed;
        state.loading = false;
        toast.error(action.payload || "Failed to update EmailTemplate order");
        state.error = action.payload || "Failed to update EmailTemplate order";
      });
  },
});

export const selectEmailTemplate = (state: RootState) => state.emailTemplate;

export default EmailTemplateSlice.reducer;
